import { skipToken } from '@apollo/client/react'
import { parseModelUrl } from 'pixai-utils/url'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'
import { withoutTrailingSlash } from 'ufo'
import Empty from 'src/components/Empty'
import ModelDetail from 'src/components/GenerationModel/GenerationModelDetail/ModelDetail'
import LoadingPage from 'src/components/LoadingPage'
import { useRenderTracker } from 'src/features/RenderTracker/useRenderTracker'
import {
  type GenerationModelDetailFragment,
  type GenerationModelVersion,
} from 'src/services/apollo/generated/graphql'
import {
  useGetGenerationModel,
  useGetGenerationModelByVersionId,
} from 'src/services/apollo/generation-model'
import { useErrorCodeTranslator } from 'src/utils/errorCodeTranslator'
import { getErrorCode } from 'src/utils/errorUtil'
import { createModelUrl } from 'src/utils/model'
import { useLocation } from 'src/utils/router/route'

export { ModelDetailPage as Component }
function ModelDetailPage(props: {
  initialModel?: GenerationModelDetailFragment
  initialVersion?: GenerationModelVersion
}) {
  const { t } = useTranslation(['common', 'market'])
  const location = useLocation()
  const { modelId, modelSlug, versionId } =
    parseModelUrl(withoutTrailingSlash(location.pathname)) ?? {}
  const {
    data: modelData,
    loading: loadingModel,
    error: modelError,
  } = useGetGenerationModel(modelId || skipToken, {
    context: { refetchOnFocus: false },
  })
  const translateErrorCode = useErrorCodeTranslator()
  const { data: versionData, error: versionError } =
    useGetGenerationModelByVersionId(versionId || skipToken, {
      context: { refetchOnFocus: false },
    })

  const error = versionId ? versionError : modelError
  const primaryModel = versionData?.model ?? modelData
  const model =
    (primaryModel?.title ? primaryModel : undefined) ??
    (props.initialVersion?.model?.title
      ? props.initialVersion.model
      : undefined) ??
    props.initialModel
  const resolvedVersion =
    (versionData?.name ? versionData : undefined) ?? props.initialVersion

  useRenderTracker({
    pageName: 'model-detail',
    ready: !loadingModel && typeof model === 'object' && model !== null,
  })

  const errMsg = error
    ? getErrorCode(error)
      ? translateErrorCode(getErrorCode(error))
      : error.message
    : 'Model does not exist'

  return (
    <>
      {model ? (
        // Redirect legacy URLs with slug to ID-only URLs.
        // This consolidates indexed URLs and signals to search engines that the slug URLs are deprecated.
        modelSlug ? (
          <Navigate
            to={createModelUrl(
              model,
              resolvedVersion?.id === versionId ? resolvedVersion : versionId,
            )}
            replace
          />
        ) : (
          <>
            {/*
              JSON-LD for model detail is emitted in route `meta()` (see
              src/routes/_app.model.$modelIdSlug.($versionId).tsx) so it lands
              in <head> during the first SSR flush without depending on Apollo
              hydration.
            */}
            <ModelDetail model={model} version={resolvedVersion} isSinglePage />
          </>
        )
      ) : loadingModel ? (
        <LoadingPage />
      ) : (
        <Empty description={errMsg} />
      )}
    </>
  )
}
